import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Typography,
  IconButton,
  Card
} from '@mui/material';
import { useTranslationHook } from '../../hooks';
import { useFormStyles } from '../../hooks/useFormStyles';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";

interface InputDialogProps {
  open: boolean;
  title: string;
  onConfirm: (text: string, file: File | null) => void;
  onCancel: () => void;
  defaultFile?: File;
  children?: React.ReactNode;
}

const InputDialog: React.FC<InputDialogProps> = ({ open, title, onConfirm, onCancel, defaultFile, children }) => {
  const { t } = useTranslationHook();
  const formStyles = useFormStyles();
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (open) {
      setFile(defaultFile || null);
      setText('');
    }
  }, [open, defaultFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  const handleConfirm = () => {
    onConfirm(text, file);
  };

  const handleCancel = () => {
    setText('');
    setFile(null);
    onCancel();
  }

  const handleDeleteFile = () => {
    setFile(null);
  }

  const handleDialogClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason && reason === "backdropClick") {
      return;
    }
    handleCancel();
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleDialogClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {children && (
          <Box sx={{ mb: 2, mt: 3 }}>
            {children}
          </Box>
        )}
        <TextField
            autoFocus
            label={t("generic.message")}
            fullWidth
            multiline
            rows={8}
            variant="outlined"
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={{ mt: 1, ...formStyles}}
        />
        <Box {...getRootProps()} sx={{ mt: 2, p: 3, border: '2px dashed grey', borderRadius: 1, textAlign: 'center', cursor: 'pointer' }}>
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 40, color: "gray"}} />
          <Typography>
            {isDragActive ? t("generic.drop-here") : t("generic.drag-or-click-pdf")}
          </Typography>
        </Box>
        {file && (
          <Card variant="outlined" sx={{ mt: 1, p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsertDriveFileIcon sx={{ color: "gray" }} />
              <Typography variant="body2">{file.name}</Typography>
            </Box>
            <IconButton onClick={handleDeleteFile} color="error" size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('inputDialog.no-additional-input')}</Button>
        <Button onClick={handleConfirm} autoFocus>
          {t('generic.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InputDialog;
